import React, { useState, useEffect, useRef } from "react";
import { useDispatch } from "react-redux";
import dayjs from "dayjs";
import { updateAppointment } from "../redux/slices/appointmentSlice";
import { AppDispatch } from "../redux/store";
import { AppointmentState, ShopSchedule } from "../types";
import { sendToWindowDataLayer } from "../utils/dom";
import {
  dayRow,
  buttonPrimary,
  buttonSecondary,
  cardHeaderClass,
  cardBodyClass,
} from "css";
import {
  SlotInfo,
  AvailableSlotsInterfaceV2,
} from "types/available-slots.interface";

interface DatePickerProps {
  appointment: AppointmentState;
  selectedDate: string | null;
  setSelectedDate: (dateKey: string | null) => void;
  startDate: Date;
  setStartDate: (date: Date) => void;
  dayAvailability: { [date: string]: boolean };
  findingAvailableDay: boolean;
  availabilityResponse?: AvailableSlotsInterfaceV2;
  loadingDates?: boolean;
}

interface WindowSize {
  width: number;
  height: number;
}

const useWindowSize = (): WindowSize => {
  const [windowSize, setWindowSize] = useState<WindowSize>({
    width: window.innerWidth,
    height: window.innerHeight,
  });

  useEffect(() => {
    const handleResize = () => {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return windowSize;
};

export const DatePicker: React.FC<DatePickerProps> = ({
  appointment,
  selectedDate,
  setSelectedDate,
  startDate,
  setStartDate,
  dayAvailability,
  findingAvailableDay,
  availabilityResponse,
  loadingDates,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const [tooltipDate, setTooltipDate] = useState<string | null>(null);
  const [page, setPage] = useState<number>(0);
  const [containerWidth, setContainerWidth] = useState<number>(0);
  const [totalOffset, setTotalOffset] = useState<number>(0);
  const containerRef = useRef<HTMLDivElement>(null);

  // Window size hook
  const { width } = useWindowSize();

  // Calculate days to show based on container width
  useEffect(() => {
    const updateContainerWidth = () => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.offsetWidth);
      }
    };

    // Initial measurement
    updateContainerWidth();

    // Re-measure on resize
    window.addEventListener("resize", updateContainerWidth);
    return () => window.removeEventListener("resize", updateContainerWidth);
  }, []);

  // Calculate how many days can fit
  const getDaysToShow = () => {
    // If container width isn't set yet, return a default
    if (!containerWidth) return width < 640 ? 3 : 5;

    // More precise sizing calculations
    const dateWidth = width < 640 ? 60 : 80;
    const dateMargin = width < 640 ? 4 : 8; // Total margin (left + right)
    const navButtonWidth = width < 640 ? 32 : 40;
    const navButtonMargin = width < 640 ? 4 : 8;

    // Total space needed for each date cell (including margins)
    const dateSpace = dateWidth + dateMargin * 2; // Make sure we account for margin on both sides

    // Space for both nav buttons (including margins)
    const navSpace = navButtonWidth * 2 + navButtonMargin * 2;

    // Available width for date cells
    const availableWidth = containerWidth - navSpace;

    // How many dates can fit
    const maxDays = Math.floor(availableWidth / dateSpace);

    // Ensure we show at least 1 day, but not more than 7
    return Math.max(1, Math.min(7, maxDays));
  };
  const daysToShow = getDaysToShow();

  // Get the actual number of visible days
  const getVisibleDaysCount = () => {
    if (!availabilityResponse || !availabilityResponse.dates) return daysToShow;
    return Math.min(daysToShow, Object.keys(availabilityResponse.dates).length);
  };

  const visibleDays = getVisibleDaysCount();

  // Modify loadDates to use visibleDays
  const loadDates = (days: number) => {
    const newTotalOffset = totalOffset + days;

    if (newTotalOffset < 0) {
      // If trying to go before the start, reset to the beginning
      setStartDate(dayjs().add(1, "day").startOf("day").toDate()); // Or your original start date
      setPage(0);
      setTotalOffset(0);
    } else {
      // Regular navigation
      setPage(page + days);
      setStartDate(dayjs(startDate).add(days, "day").toDate());
      setTotalOffset(newTotalOffset);
    }
  };

  // Calculate available slots count for a given date
  const getAvailableSlotsCount = (
    slotsArray: SlotInfo[] | undefined
  ): number => {
    if (!Array.isArray(slotsArray)) return 0;
    return slotsArray.filter((slot) => slot.isAvailable).length;
  };

  return (
    <>
      <div
        id="date-picker"
        className={`${cardHeaderClass} flex justify-between`}
        style={{
          backgroundColor: appointment?.shopData?.color,
        }}
      >
        Select a Date
        <span className="relative flex h-3 w-3 pt-1.5">
          {selectedDate ? (
            <>
              <span className="relative inline-flex rounded-full h-3 w-3 bg-green-600"></span>
            </>
          ) : (
            <>
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-3 w-3 bg-red-600"></span>
            </>
          )}
        </span>
      </div>
      <div
        className={cardBodyClass}
        style={{
          borderColor: appointment?.shopData?.color,
        }}
        ref={containerRef}
      >
        {findingAvailableDay ? (
          <div className="col-span-3 md:col-span-6">
            <center>
              <img
                src="https://bdm-files.s3.amazonaws.com/images/spinners/Gear@1x-0.3s-200px-200px.gif"
                alt="loading"
              />
              <br />
              <br />
              Finding the best times for you!
            </center>
          </div>
        ) : loadingDates ? (
          <div className="col-span-3 md:col-span-6">
            <div className="flex flex-col items-center justify-center">
              <img
                src="https://bdm-files.s3.amazonaws.com/images/spinners/Gear@1x-0.3s-200px-200px.gif"
                alt="loading"
              />
              <p className="text-sm text-gray-600 mt-3">
                Loading available dates...
              </p>
            </div>
          </div>
        ) : (
          <div
            className={dayRow}
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center", // Changed to center for better alignment
              flexWrap: "nowrap",
              minHeight: width < 640 ? "76px" : "96px", // Set a min height
            }}
          >
            <div style={{ alignSelf: "center" }}>
              <div
                style={{
                  width: width < 640 ? "32px" : "40px",
                  marginRight: width < 640 ? "4px" : "8px",
                  height: width < 640 ? "60px" : "80px",
                  borderRadius: "0",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  border: `1px solid ${appointment?.shopData?.color}`,
                  boxShadow:
                    appointment?.shopData?.shadow === "shadow-md"
                      ? `0 4px 6px ${appointment?.shopData?.color}33`
                      : "none",
                }}
                className={`${buttonPrimary} disabled`}
                onClick={() => loadDates(-visibleDays)} // Use visibleDays instead of daysToShow
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="currentColor"
                  style={{
                    width: width < 640 ? "16px" : "24px",
                    height: width < 640 ? "16px" : "24px",
                  }}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M15.75 19.5 8.25 12l7.5-7.5"
                  />
                </svg>
              </div>
            </div>

            {availabilityResponse &&
              availabilityResponse.dates &&
              Object.entries(availabilityResponse.dates)
                .slice(0, daysToShow)
                .map(([dateKey, waitersAndDrop], index) => {
                  const slotsArray =
                    appointment.apptType === "wait"
                      ? waitersAndDrop.waiterSlots
                      : waitersAndDrop.dropOffSlots;
                  const availableSlotsCount =
                    getAvailableSlotsCount(slotsArray);

                  const hasAvailableSlots = availableSlotsCount > 0;

                  return (
                    <div
                      key={index}
                      data-service-date={dateKey}
                      style={{
                        display: "flex",
                        justifyContent: "center",
                        textAlign: "center",
                        alignItems: "center",
                        margin: width < 640 ? "0 2px" : "0 4px",
                        width: width < 640 ? "60px" : "80px",
                        height: width < 640 ? "60px" : "80px",
                        position: "relative",
                      }}
                      onMouseEnter={() => setTooltipDate(dateKey)}
                      onMouseLeave={() => setTooltipDate(null)}
                    >
                      <div
                        style={{
                          width: "100%",
                          cursor: hasAvailableSlots ? "pointer" : "not-allowed",
                        }}
                        onClick={() => {
                          if (hasAvailableSlots) {
                            dispatch(
                              updateAppointment({
                                updates: {
                                  date: dayjs(dateKey).format("MM/DD/YYYY"),
                                },
                              })
                            );
                            setSelectedDate(dateKey);
                            sendToWindowDataLayer(
                              "dc-service-date",
                              dayjs(dateKey).format("ddd DD MMM")
                            );
                          }
                        }}
                      >
                        <div
                          className={
                            selectedDate && dateKey === selectedDate
                              ? buttonSecondary
                              : buttonPrimary
                          }
                          style={{
                            backgroundColor:
                              selectedDate && dateKey === selectedDate
                                ? appointment?.shopData?.color
                                : "white",
                            boxShadow:
                              appointment?.shopData?.shadow === "shadow-md"
                                ? `0 4px 6px ${appointment?.shopData?.color}33`
                                : "none",
                            border: `1px solid ${appointment?.shopData?.color}`,
                            color:
                              selectedDate && dateKey === selectedDate
                                ? "white"
                                : "black",
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "center",
                            alignItems: "center",
                            height: width < 640 ? "60px" : "80px",
                            width: "100%",
                            opacity: hasAvailableSlots ? 1 : 0.5,
                          }}
                        >
                          <div
                            style={{
                              textAlign: "center",
                              fontSize: width < 640 ? "12px" : "14px",
                              lineHeight: "1.2",
                            }}
                          >
                            {dayjs(dateKey).format("ddd")}
                          </div>
                          <div
                            style={{
                              textAlign: "center",
                              fontSize: width < 640 ? "16px" : "18px",
                              fontWeight: "bold",
                              lineHeight: "1.2",
                            }}
                          >
                            {dayjs(dateKey).format("DD")}
                          </div>
                          <div
                            style={{
                              textAlign: "center",
                              fontSize: width < 640 ? "12px" : "14px",
                              lineHeight: "1.2",
                            }}
                          >
                            {dayjs(dateKey).format("MMM")}
                          </div>
                        </div>
                      </div>

                      {/* Tooltip showing available slots count */}
                      {tooltipDate === dateKey && hasAvailableSlots && (
                        <div
                          className="absolute -top-10 left-1/2 transform -translate-x-1/2 bg-gray-800 text-white px-2 py-1 rounded text-sm whitespace-nowrap"
                          style={{
                            boxShadow: "0 2px 5px rgba(0,0,0,0.2)",
                          }}
                        >
                          {availableSlotsCount}{" "}
                          {availableSlotsCount === 1 ? "slot" : "slots"}{" "}
                          available
                          <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-800"></div>
                        </div>
                      )}
                    </div>
                  );
                })}
            <div style={{ alignSelf: "center" }}>
              <div
                style={{
                  width: width < 640 ? "32px" : "40px",
                  marginLeft: width < 640 ? "4px" : "8px",
                  height: width < 640 ? "60px" : "80px",
                  borderRadius: "0",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: "white",
                  boxShadow:
                    appointment?.shopData?.shadow === "shadow-md"
                      ? `0 4px 6px ${appointment?.shopData?.color}33`
                      : "none",
                  border: `1px solid ${appointment?.shopData?.color}`,
                  color: "black",
                }}
                className={buttonPrimary}
                onClick={() => loadDates(visibleDays)} // Use visibleDays instead of daysToShow
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="currentColor"
                  style={{
                    width: width < 640 ? "16px" : "24px",
                    height: width < 640 ? "16px" : "24px",
                  }}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="m8.25 4.5 7.5 7.5-7.5 7.5"
                  />
                </svg>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};
